/**
 * Fixed-timestep game loop.
 *
 * The simulation advances in fixed steps of `tickSeconds` (20 Hz, like the 2013
 * `intervalle_horloge = 0.05f`), decoupled from rendering which runs on
 * `requestAnimationFrame` with interpolation. This keeps the sim deterministic
 * regardless of display refresh rate — the original used `clock()` (CPU time),
 * which drifted on fast machines.
 */

export interface LoopCallbacks {
  /** Advance the simulation by one fixed step. */
  update: () => void;
  /** Draw the current state; `alpha` in [0,1] is the fraction of a step elapsed. */
  render: (alpha: number) => void;
}

export class GameLoop {
  private readonly stepMs: number;
  private accumulator = 0;
  private lastTime = 0;
  private running = false;
  private frame = 0;

  constructor(
    tickSeconds: number,
    private readonly cb: LoopCallbacks,
  ) {
    this.stepMs = tickSeconds * 1000;
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.frame = requestAnimationFrame(this.tick);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.frame);
  }

  private readonly tick = (now: number): void => {
    if (!this.running) return;
    this.frame = requestAnimationFrame(this.tick);

    let delta = now - this.lastTime;
    this.lastTime = now;
    // Avoid the "spiral of death" after a tab switch or a long stall.
    if (delta > 250) delta = 250;

    this.accumulator += delta;
    while (this.accumulator >= this.stepMs) {
      this.cb.update();
      this.accumulator -= this.stepMs;
    }
    this.cb.render(this.accumulator / this.stepMs);
  };
}
